// @ts-strict-ignore
import React, { useState, useEffect, useRef, ChangeEvent } from 'react'
import { Switch } from '../ui/switch'
import { Icon } from '@blueprintjs/core'
import { Table } from '../ui/Table'
import { InlineTextInput } from '../ui/InlineTextInput'
import ReactCountryFlag from 'react-country-flag'
import { cents } from '../../util/formatters'
import { useSelector, useDispatch } from 'react-redux'
import { axiosGet, axiosPatch } from '../../util/api'
import { useUser } from '../../hooks/useUser'
import { clearAndFetchUser } from '../../actions/user'
import { ApiAccessCell } from './ApiAccessCell'
import classNames from 'classnames'
import { Marketplace, Marketplaces } from '../../util/marketplaces'
import { Toasts } from '../toaster/Toast'
import { useTableHooks } from '../../packages/blueprint-components/src/useTableHooks'
import { DefaultProfileSelect } from './DefaultProfileSelect'
import { Tooltip } from '../ui/tooltip'
import { State } from '../../reducers'
import Profile = Models.Profile
import { type EntityId, idKey } from '../../types/constants'
import { Column } from '../../types/table'
import { FilterI, Operator } from '../../constants/filter'
import { Button } from '../ui/button'
import { Filter } from '../Filter'

// ── Filters ───────────────────────────────────────────────────────────────────

const profileFilters: FilterI[] = [
    {
        key: 'spend',
        name: 'Ad Spend (last 30 days)',
        type: 'number',
        operators: [
            Operator.GreaterThanOrEqualTo,
            Operator.LessThanOrEqualTo,
            Operator.GreaterThan,
            Operator.LessThan,
            Operator.EqualTo,
            Operator.NotEqualTo,
            Operator.IsNull,
        ],
        prefix: '$',
    },
    {
        key: 'managed',
        name: 'Managed',
        type: 'select',
        options: [
            { name: 'Managed', value: 'true' },
            { name: 'Not managed', value: 'false' },
        ],
    },
    {
        key: 'connected',
        name: 'API Access',
        type: 'select',
        options: [
            { name: 'Connected', value: 'true' },
            { name: 'Disconnected', value: 'false' },
        ],
    },
]

// ── Main component ────────────────────────────────────────────────────────────

export const ProfilesTableNew = ({
    stickyFooter = true,
    showDefaultProfile = true,
    showManaged = true,
    showApiAccess = true,
    showRefresh = false,
    emptyMessage,
    reloadToken = 0,
}: {
    stickyFooter?: boolean
    showDefaultProfile?: boolean
    showManaged?: boolean
    showApiAccess?: boolean
    showRefresh?: boolean
    emptyMessage?: React.ReactNode
    reloadToken?: number
}) => {
    const load = (params: any) => axiosGet('profiles', params)

    const dispatch = useDispatch()
    const { hasActiveSubscription, settings } = useUser()
    const tableHooks = useTableHooks<Profile>(load)
    const [loading, setLoading] = useState(false)
    const [refreshLoading, setRefreshLoading] = useState(false)
    const [defaultProfile, setDefaultProfile] = useState<EntityId | null>(null)
    const screenshotMode = useSelector((store: State) => store.user.screenshot_mode)

    useEffect(() => {
        if (settings) {
            setDefaultProfile(settings.default_profile)
            tableHooks.reload()
        }
    }, [settings])

    useEffect(() => {
        if (reloadToken > 0) tableHooks.reload()
    }, [reloadToken])

    const refresh = () => {
        setRefreshLoading(true)
        axiosGet('profiles/refresh')
            .then(() => {
                dispatch(clearAndFetchUser())
                tableHooks.reload()
                setRefreshLoading(false)
            })
            .catch(() => setRefreshLoading(false))
    }

    const saveNickname = (payload: Profile) => {
        setLoading(true)
        axiosPatch(`profiles/${payload.profile_id}`, payload)
            .then((_) => {
                dispatch(clearAndFetchUser())
                setLoading(false)
                tableHooks.mutateItems((items) =>
                    items.map((p) => {
                        if (idKey(p.profile_id) === idKey(payload.profile_id)) {
                            p.nickname = payload.nickname
                        }
                        return p
                    }),
                )
                Toasts.success('Nickname saved')
            })
            .catch(() => setLoading(false))
    }

    const saveManaged = (payload: Profile) => {
        axiosPatch(`profiles/${payload.profile_id}`, payload).then((_) => {
            dispatch(clearAndFetchUser())
            Toasts.success(payload.managed ? 'Profile activated' : 'Profile deactivated')
        })
    }

    const onAfterRecheck = (updatedProfile: Profile) => {
        tableHooks.mutateItems((items) =>
            items.map((p) =>
                idKey(p.profile_id) === idKey(updatedProfile.profile_id)
                    ? { ...p, unlinked_at: updatedProfile.unlinked_at }
                    : p,
            ),
        )
    }

    const columns: Column<Profile>[] = [
        {
            key: 'country_code',
            title: <></>,
            style: { width: '44px', maxWidth: '44px' },
            cell: (profile) => {
                const countryCode = profile.country_code === 'UK' ? 'gb' : profile.country_code
                return (
                    <ReactCountryFlag
                        countryCode={countryCode}
                        svg
                        className="text-2xl"
                        style={{ width: '1.5em', height: '1.5em' }}
                    />
                )
            },
        },
        {
            key: 'name',
            title: 'Account',
            cell: (profile) => {
                const isDefault = idKey(profile.profile_id) === idKey(defaultProfile)
                return (
                    <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-1.5 min-w-0">
                            <span
                                className={classNames(
                                    'text-[15px] font-[500] tracking-[-0.01em] text-[#0E2947] truncate leading-snug',
                                    { blur: screenshotMode },
                                )}
                            >
                                {profile.name}
                            </span>
                            {isDefault && (
                                <span className="flex-shrink-0 text-[11px] font-medium text-[#7289A3] bg-[#F1F3F6] px-1.5 py-0.5 rounded-md leading-none">
                                    Default
                                </span>
                            )}
                        </div>
                        <span
                            className={classNames(
                                'text-[12px] text-[#94A3B8] leading-none mt-0.5 truncate',
                                { blur: screenshotMode },
                            )}
                        >
                            {profile.entity_id} · {profile.profile_id}
                        </span>
                    </div>
                )
            },
        },
        {
            key: 'spend',
            title: (
                <Tooltip
                    content={
                        <div style={{ maxWidth: '400px' }}>
                            Total spend Amazon charged over the past 30 days. May differ from actual
                            ad spend since Amazon bills in different increments.
                        </div>
                    }
                >
                    <span className="cursor-default">30d Spend</span>
                </Tooltip>
            ),
            headerStyle: { justifyContent: 'flex-end' },
            style: { textAlign: 'right' },
            cell: (profile) => {
                const m = Marketplaces[profile.marketplace_id as Marketplace]
                return (
                    <span
                        className={classNames('text-[14px] text-[#0E2947]', {
                            blur: screenshotMode,
                        })}
                    >
                        {cents(profile.total_ad_spend_30_days, m)}
                    </span>
                )
            },
        },
        {
            key: 'nickname',
            title: 'Nickname',
            cell: (profile) => (
                <NicknameCell
                    key={profile.profile_id}
                    nickname={profile?.nickname ?? ''}
                    loading={loading}
                    screenshotMode={screenshotMode}
                    onSave={(nickname) => saveNickname({ ...profile, nickname })}
                />
            ),
        },
        showApiAccess && {
            key: 'unlinked_at',
            title: (
                <Tooltip
                    content={
                        <div style={{ maxWidth: '300px' }}>
                            Whether MerchJar can currently reach this profile via Amazon's API. If
                            disconnected, click <b>Re-check</b> after re-authorizing in Amazon Ads.
                        </div>
                    }
                >
                    <span className="cursor-default">API Access</span>
                </Tooltip>
            ),
            headerStyle: { justifyContent: 'flex-end' },
            style: { textAlign: 'right' },
            cell: (profile) => <ApiAccessCell profile={profile} onAfterRecheck={onAfterRecheck} />,
        },
        showManaged && {
            key: 'managed',
            title: (
                <Tooltip
                    content={
                        <div>
                            Merch Jar will pull in data and allow you to perform bulk operations or
                            setup automations on any managed profile.
                        </div>
                    }
                >
                    <span className="cursor-default">Managed</span>
                </Tooltip>
            ),
            headerStyle: { justifyContent: 'flex-end' },
            style: { textAlign: 'right' },
            cell: (profile) => (
                <div className="flex justify-end">
                    <Tooltip
                        content={
                            <div>
                                Managed profiles cannot be changed without an active subscription
                            </div>
                        }
                        disabled={hasActiveSubscription}
                    >
                        <Switch
                            disabled={!hasActiveSubscription}
                            onChange={(managed: ChangeEvent<any>) =>
                                saveManaged({
                                    ...profile,
                                    managed: managed.target.checked,
                                })
                            }
                            checked={profile.managed}
                        />
                    </Tooltip>
                </div>
            ),
        },
    ].filter(Boolean) as Column<Profile>[]

    return (
        <div>
            {/* ── Controls bar ─────────────────────────────────────────────── */}
            <div className="flex items-center mb-0">
                <div className="flex items-center gap-2">
                    <Filter
                        filters={profileFilters}
                        customFilters={[]}
                        saveFilter={() => {}}
                        deleteCustomFilter={() => Promise.resolve()}
                        editCustomFilter={() => {}}
                        onChange={(f) => tableHooks.setFilter(f)}
                        defaultActiveFilters={{}}
                        triggerClassName="border border-[#DBE2E8] !shadow-none"
                        maxTagWidth={380}
                    />
                </div>
                <div className="flex items-center gap-2 ml-auto">
                    {showDefaultProfile && (
                        <div className="flex items-center gap-1.5">
                            <span className="text-[13px] text-[#7289A3] whitespace-nowrap">
                                Default profile
                            </span>
                            <DefaultProfileSelect condensed buttonProps={{ outlined: true }} />
                        </div>
                    )}
                    <SearchInput
                        loading={tableHooks.loading}
                        onSearch={(q) => tableHooks.setQuery(q)}
                        placeholder="Search"
                        maxWidth={260}
                    />
                    {showRefresh && (
                        <Button
                            outlined
                            text="Refresh"
                            icon="refresh"
                            loading={refreshLoading}
                            onClick={refresh}
                        />
                    )}
                </div>
            </div>

            {/* ── Table ────────────────────────────────────────────────────── */}
            <div className="[&_td]:!py-2 [&_th]:!py-0.5 [&_th>div]:!py-1 [&_#table-bottom-section]:!mt-0 [&_#table-bottom-section]:!mb-1 [&_#table-bottom-section]:!py-1.5 [&_#table-bottom-section]:!items-center [&_.bp4-card-rounded]:!shadow-[0_0_0_1px_#DBE2E8]">
                <Table
                    name="Profiles"
                    striped={false}
                    rowDividers
                    showHeader={false}
                    onSelectChange={null}
                    tableHooks={tableHooks}
                    columns={columns}
                    canSelect={false}
                    columnSelection={false}
                    stickyFooter={stickyFooter}
                    empty={
                        emptyMessage ? (
                            <tbody>
                                <tr>
                                    <td colSpan={columns.length}>
                                        <div className="px-4 py-8 text-center text-[14px] leading-5 text-[#7289A3]">
                                            {emptyMessage}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        ) : null
                    }
                />
            </div>
        </div>
    )
}

// ── NicknameCell ──────────────────────────────────────────────────────────────

const NicknameCell = ({
    nickname,
    loading,
    screenshotMode,
    onSave,
}: {
    nickname: string
    loading: boolean
    screenshotMode: boolean
    onSave: (nickname: string) => void
}) => {
    const [value, setValue] = useState(nickname)

    useEffect(() => {
        setValue(nickname)
    }, [nickname])

    return (
        <InlineTextInput
            value={value}
            onChange={setValue}
            onBlur={onSave}
            disabled={loading}
            placeholder="Add nickname"
            className={classNames(
                'text-sm font-medium placeholder:text-[#94A3B8] placeholder:italic',
                { blur: screenshotMode },
            )}
        />
    )
}

// ── SearchInput ───────────────────────────────────────────────────────────────

const SearchInput = ({
    loading,
    onSearch,
    placeholder = 'Search',
    maxWidth,
}: {
    loading?: boolean
    onSearch: (q: string) => void
    placeholder?: string
    maxWidth?: number
}) => {
    const [query, setQuery] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)

    return (
        <div
            className="relative w-full"
            style={{ maxWidth: maxWidth ? `${maxWidth}px` : undefined }}
        >
            <svg
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none"
                width="13"
                height="13"
                viewBox="0 0 16 16"
                fill="none"
            >
                <path
                    d="M7 13A6 6 0 1 0 7 1a6 6 0 0 0 0 12ZM14 14l-3-3"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                />
            </svg>
            <input
                ref={inputRef}
                type="text"
                value={query}
                disabled={loading}
                placeholder={placeholder}
                className="w-full h-8 pl-8 pr-7 rounded-lg border border-[#DBE2E8] bg-white text-[14px] text-[#0E2947] placeholder-[#94A3B8] outline-none focus:border-[#3391FF] focus:ring-1 focus:ring-[#3391FF]/20 transition-colors"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSearch(query)}
            />
            {query && (
                <button
                    type="button"
                    onClick={() => {
                        setQuery('')
                        onSearch('')
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0E2947] transition-colors"
                >
                    <Icon icon="cross" size={12} />
                </button>
            )}
        </div>
    )
}
